import { Button } from '@affine/component';
import type {
  AccountDeletionPreview,
  InvoicePreview,
  SubscriptionPreview,
  WorkspacePreview,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';

import * as styles from './style.css';

export interface StepInfoProps {
  preview: AccountDeletionPreview;
  onCancel: () => void;
  onNext: () => void;
}

// Step 1 keeps the decision compact while preserving concrete billing,
// workspace and statutory-retention consequences.
export const StepInfo = ({ preview, onCancel, onNext }: StepInfoProps) => {
  const t = useI18n();

  const days = String(preview.gracePeriodDays);

  return (
    <div className={styles.dialogContent}>
      <div className={styles.stepHeader}>
        <span>
          {t['com.streamlain.setting.account.delete.dialog.step-indicator']({
            current: '1',
            total: '2',
          })}
        </span>
      </div>

      <h2 className={styles.sectionTitle}>
        {t['com.streamlain.setting.account.delete.step-info.title']()}
      </h2>

      <p>
        {t['com.streamlain.setting.account.delete.step-info.intro']({ days })}
      </p>

      {preview.activeSubscriptions.length > 0 ? (
        <>
          <div className={styles.inputLabel}>
            {t[
              'com.streamlain.setting.account.delete.step-info.subscriptions-label'
            ]()}
          </div>
          <ul className={styles.sectionList}>
            {preview.activeSubscriptions.map((sub: SubscriptionPreview) => (
              <li key={sub.id} className={styles.sectionListItem}>
                <span>
                  {sub.plan} · {(sub.amount / 100).toFixed(2)}{' '}
                  {sub.currency.toUpperCase()}
                </span>
                <span className={styles.memberCount}>
                  {t[
                    'com.streamlain.setting.account.delete.step-info.subscription-cancel-at'
                  ]({
                    date: new Date(sub.periodEnd).toLocaleDateString(),
                  })}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {preview.ownedWorkspaces.length > 0 ? (
        <>
          <div className={styles.inputLabel}>
            {t[
              'com.streamlain.setting.account.delete.step-info.workspaces-label'
            ]()}
          </div>
          <ul className={styles.sectionList}>
            {preview.ownedWorkspaces.map((ws: WorkspacePreview) => (
              <li key={ws.id} className={styles.sectionListItem}>
                <span>{ws.name || ws.id}</span>
                <span className={styles.memberCount}>
                  {t[
                    'com.streamlain.setting.account.delete.step-info.workspace-members'
                  ]({ count: String(ws.memberCount) })}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {preview.pendingInvoices.length > 0 ? (
        <>
          <div className={styles.inputLabel}>
            {t[
              'com.streamlain.setting.account.delete.step-info.invoices-label'
            ]()}
          </div>
          <ul className={styles.sectionList}>
            {preview.pendingInvoices.map((inv: InvoicePreview) => (
              <li key={inv.id} className={styles.sectionListItem}>
                <span>
                  {(inv.amount / 100).toFixed(2)} {inv.currency.toUpperCase()}
                </span>
                <span className={styles.memberCount}>
                  {t[
                    'com.streamlain.setting.account.delete.step-info.invoice-due'
                  ]({
                    date: new Date(inv.dueDate).toLocaleDateString(),
                  })}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <div className={styles.complianceHint}>
        <strong>
          {t[
            'com.streamlain.setting.account.delete.step-info.compliance-title'
          ]()}
        </strong>
        <span>
          {t[
            'com.streamlain.setting.account.delete.step-info.compliance-body'
          ]()}
        </span>
      </div>

      <div className={styles.footer}>
        <Button variant="plain" onClick={onCancel}>
          {t['com.streamlain.setting.account.delete.dialog.cancel']()}
        </Button>
        <Button variant="primary" onClick={onNext}>
          {t['com.streamlain.setting.account.delete.dialog.next']()}
        </Button>
      </div>
    </div>
  );
};
